import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonButton,
  IonContent,
  IonDatetime,
  IonFooter,
  IonModal,
  IonPicker,
  IonPickerColumn,
  IonPickerColumnOption,
} from '@ionic/react';
import {
  clampDuration,
  clampWindow,
  describeDuration,
  describeWindow,
  describeWindowDate,
  extractDate,
  extractTime,
  getMinDate,
  getMinDurationMin,
  getMinStartTime,
  joinDuration,
  splitDuration,
  windowsOverlap,
  withRefDate,
  type DurationBounds,
  type OriginalTimeWindow,
  type TimeWindowState,
} from '../../utils/listingTimeWindow';

export type HandoffKind = 'pickup' | 'dropoff';

/**
 * Window length as hours + quarter-hours.
 *
 * Moved here from PublishListingPage when the hand-off cards took over the
 * window UI: there are two wheels now and they don't share a range, so the
 * column options come in as `bounds` rather than module constants.
 *
 * memo'd because Ionic's IonPickerColumn fires spurious onIonChange events
 * during re-render, which produced a "ghost scroll" when sibling pickers shared
 * a parent. Each column additionally carries its own dedup ref.
 */
const DurationPicker = memo(function DurationPicker({
  durationMin,
  floorMin,
  bounds,
  hoursSuffix,
  minutesSuffix,
  onChange,
}: {
  durationMin: number;
  /** Shortest window allowed at the current start (edit-mode end floor). */
  floorMin: number;
  bounds: DurationBounds;
  /** Translated column suffixes. Threaded in so the picker stays free of i18n
   *  bindings inside `memo()`. */
  hoursSuffix: string;
  minutesSuffix: string;
  onChange: (durationMin: number) => void;
}) {
  const hourEl = useRef<HTMLIonPickerColumnElement>(null);
  const minuteEl = useRef<HTMLIonPickerColumnElement>(null);
  const { hours, minutes } = splitDuration(durationMin);
  const hourRef = useRef(hours);
  const minuteRef = useRef(minutes);
  // Keep the dedup refs in sync with prop-driven snaps, or the first scroll
  // after a parent-driven move gets swallowed by the dedup check below.
  useEffect(() => {
    hourRef.current = hours;
    minuteRef.current = minutes;
  }, [hours, minutes]);

  // Emit, then force both wheels onto the value the clamp settles on.
  //
  // clampDuration is the same function clampWindow uses, so the picker can
  // predict the result rather than waiting for a prop change that may never
  // come: on the drop-off wheel, 1 hr against 45 min settles at 1:00 and the
  // emitting column's prop is exactly where it already was. React has no change
  // to drive the wheel back with, so we push Ionic's internal value directly.
  // Deferred to the next microtask so we don't fight the scroll-settle cycle
  // that just emitted this event.
  const emit = (h: number, m: number) => {
    const settled = clampDuration(joinDuration(h, m), floorMin, bounds);
    onChange(settled);
    const next = splitDuration(settled);
    if (next.hours === h && next.minutes === m) return;
    queueMicrotask(() => {
      hourRef.current = next.hours;
      minuteRef.current = next.minutes;
      hourEl.current?.setValue(next.hours);
      minuteEl.current?.setValue(next.minutes);
    });
  };

  return (
    <IonPicker>
      <IonPickerColumn
        ref={hourEl}
        value={hours}
        onIonChange={(e) => {
          const v = e.detail.value;
          if (typeof v !== 'number' || v === hourRef.current) return;
          hourRef.current = v;
          emit(v, minuteRef.current);
        }}
      >
        <div slot="suffix" className="text-sm text-gray-500">{hoursSuffix}</div>
        {bounds.hourOptions.map((h) => (
          <IonPickerColumnOption key={h} value={h}>{h}</IonPickerColumnOption>
        ))}
      </IonPickerColumn>
      <IonPickerColumn
        ref={minuteEl}
        value={minutes}
        onIonChange={(e) => {
          const v = e.detail.value;
          if (typeof v !== 'number' || v === minuteRef.current) return;
          minuteRef.current = v;
          emit(hourRef.current, v);
        }}
      >
        <div slot="suffix" className="text-sm text-gray-500">{minutesSuffix}</div>
        {bounds.minuteOptions.map((m) => (
          <IonPickerColumnOption key={m} value={m}>
            {String(m).padStart(2, '0')}
          </IonPickerColumnOption>
        ))}
      </IonPickerColumn>
    </IonPicker>
  );
});

/** Which of the three values the chef is editing; null when nothing is open. */
type EditingField = 'date' | 'start' | 'duration';

/**
 * Sheet height per control. The calendar is roughly twice the height of a
 * wheel, so one shared value either cramped the calendar or left the wheels
 * floating above a dead gap. Read at present time, and `editing` is always set
 * in the same render that flips the sheet open, so the right value is in place.
 * Content still scrolls (expandToScroll below), so these are comfort, not
 * correctness — a large text size just scrolls rather than clipping.
 */
const SHEET_HEIGHT: Record<EditingField, number> = {
  date: 0.85,
  start: 0.6,
  duration: 0.6,
};

/**
 * One hand-off window, stated as a sentence and edited a value at a time.
 *
 * The three wheels used to sit open on the publish page, which was workable
 * with one window and would have been three screens tall with two. The card
 * states the window instead ("Tonight from 6:30 PM for 2 hr"), and each value
 * is its own tap target opening its own sheet: the day opens a calendar, the
 * time opens a clock, the length opens the length wheel. One decision per
 * screen, rather than three wheels competing for the same glance.
 *
 * Constraint logic stays in utils/listingTimeWindow: every edit is routed
 * through clampWindow with this card's own `bounds`, which is what holds a
 * drop-off run to its hour cap while pickup keeps the full range.
 */
export function HandoffWindowCard({
  kind,
  window,
  bounds,
  original,
  otherWindow,
  locale,
  disabled = false,
  onChange,
}: {
  kind: HandoffKind;
  window: TimeWindowState;
  bounds: DurationBounds;
  /** The persisted window an edit is measured against (published listings). */
  original?: OriginalTimeWindow;
  /** The other selected hand-off window, if any. The draft is checked against
   *  it so Done can be refused rather than letting the chef save an overlap. */
  otherWindow?: TimeWindowState | null;
  locale: string;
  disabled?: boolean;
  /** Receives the already-clamped window, once, when Done is pressed. */
  onChange: (next: TimeWindowState) => void;
}) {
  const { t } = useTranslation('publish');
  const [editing, setEditing] = useState<EditingField | null>(null);

  // Edits are held here until Done, rather than being pushed up per wheel tick.
  //
  // Live commits had two problems. The cards are ordered by start time, so
  // scrolling a wheel past the other window re-sorted the list mid-gesture and
  // tore the open sheet down underneath the chef's finger. And a per-tick commit
  // has nowhere to refuse: every intermediate value was already saved, so an
  // overlap could only be reported after the fact by the publish blocker.
  // Buffering makes Done the single commit point, which is what gives us
  // somewhere to stand and say no.
  const [draft, setDraft] = useState<TimeWindowState | null>(null);

  // The card always shows the committed value; only the sheet shows the draft.
  // That's what keeps the card still (and the sort stable) while wheels move.
  const shown = draft ?? window;

  // Ionic's colour roles rather than raw hex so the card follows the theme:
  // secondary reads as the "at home" blue, tertiary as the "on the road" gold.
  const accent = kind === 'pickup' ? 'secondary' : 'tertiary';
  const accentColor = `var(--ion-color-${accent})`;
  const accentContrast = `var(--ion-color-${accent}-contrast)`;

  const summary = describeWindow(window, locale);
  const dateLabel = describeWindowDate(window, locale);
  const dateText = dateLabel.relativeKey
    ? t(`handoff.${dateLabel.relativeKey}`)
    : dateLabel.formatted;
  const durationText = describeDuration(
    window.durationMin,
    t('duration.hoursSuffix'),
    t('duration.minutesSuffix'),
  );

  const draftSummary = describeWindow(shown, locale);
  const minDurationMin = getMinDurationMin(shown.date, shown.startTime, original, bounds);

  // Half-open, so a draft that starts exactly when the other window ends is
  // adjacent and fine. Same rule the backend's publish gate applies.
  const conflicts = !!otherWindow && windowsOverlap(shown, otherWindow);

  const edit = (partial: Partial<TimeWindowState>) => {
    setDraft(clampWindow({ ...shown, ...partial }, original, bounds));
  };

  const openField = (field: EditingField) => {
    setDraft(window);
    setEditing(field);
  };

  const close = () => {
    setEditing(null);
    setDraft(null);
  };

  const confirm = () => {
    if (conflicts) return;
    if (draft) onChange(draft);
    close();
  };

  const badge = (
    <span
      className="rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide"
      style={{ backgroundColor: accentColor, color: accentContrast }}
    >
      {t(`handoff.${kind}Badge`)}
    </span>
  );

  // Each value is a button, so the card itself can't be one (nested buttons are
  // invalid and swallow the inner taps).
  const valueButton = (field: EditingField, label: string, ariaKey: string) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => openField(field)}
      aria-label={t(ariaKey)}
      className="rounded-md px-1 text-lg font-semibold text-foreground underline decoration-dotted underline-offset-4 transition-opacity active:opacity-70 disabled:no-underline disabled:opacity-60"
    >
      {label}
    </button>
  );

  const modalTitle =
    editing === 'date'
      ? t('handoff.pickDay')
      : editing === 'start'
        ? t('handoff.pickStart')
        : t('handoff.pickDuration');

  const sheetHeight = editing ? SHEET_HEIGHT[editing] : SHEET_HEIGHT.duration;

  return (
    <>
      <div
        className="w-full rounded-xl border bg-card px-4 py-3"
        style={{ borderColor: accentColor }}
      >
        <div className="flex items-center gap-2">
          {badge}
          <span className="text-sm text-muted-foreground">
            {t(`handoff.${kind}Subtitle`)}
          </span>
        </div>

        {/* The window as a sentence. The three values carry the weight and the
            connecting words recede, so the row reads as one statement rather
            than three separate controls — while still being three controls. */}
        <div className="mt-2 flex flex-wrap items-baseline gap-x-1 gap-y-1">
          {valueButton('date', dateText, 'handoff.editDay')}
          <span className="text-sm text-muted-foreground">{t('handoff.from')}</span>
          {valueButton('start', summary.startTime, 'handoff.editStart')}
          <span className="text-sm text-muted-foreground">{t('handoff.for')}</span>
          {valueButton('duration', durationText, 'handoff.editDuration')}
        </div>

        {/* The end is derived, so it has to be stated rather than left for the
            chef to work out. An overnight window names the date too, since
            "ends 12:30 AM" alone is ambiguous about which night. */}
        <p className="mt-1 text-sm text-muted-foreground">
          {summary.crossesMidnight
            ? t('handoff.endsNextDay', {
                time: summary.endTime,
                date: summary.endDateShort,
              })
            : t('handoff.ends', { time: summary.endTime })}
        </p>
      </div>

      {/* One sheet, one decision. Which control it carries is `editing`.
          Dismissing without Done discards the draft, which is the whole point of
          buffering: the committed window is only ever replaced deliberately. */}
      <IonModal
        isOpen={editing !== null}
        initialBreakpoint={sheetHeight}
        breakpoints={[0, sheetHeight, 1]}
        handle
        // Without this, Ionic disables IonContent scrolling at any non-max
        // breakpoint and the drag gesture owns vertical movement — so at large
        // accessibility text sizes the content ends up below the fold with no
        // way to reach it. Same fix, same reason, as ActivityListModal.
        expandToScroll={false}
        onDidDismiss={close}
      >
        <IonContent>
          <div className="flex flex-col gap-4 p-4 pb-8">
            <div className="flex items-center gap-2">
              {badge}
              <span className="text-sm text-muted-foreground">{modalTitle}</span>
            </div>

            {editing === 'date' && (
              <IonDatetime
                presentation="date"
                value={shown.date}
                min={getMinDate(original)}
                onIonChange={(e) => {
                  const date = extractDate(e.detail.value);
                  if (date) edit({ date });
                }}
              />
            )}

            {editing === 'start' && (
              <div className="flex justify-center">
                <IonDatetime
                  presentation="time"
                  minuteValues="0,15,30,45"
                  value={withRefDate(shown.startTime)}
                  min={withRefDate(getMinStartTime(shown.date, original))}
                  onIonChange={(e) => {
                    const startTime = extractTime(e.detail.value);
                    // The duration rides along untouched, so moving the start
                    // slides the whole window instead of resizing it.
                    if (startTime) edit({ startTime });
                  }}
                />
              </div>
            )}

            {/* Length, not an end time. A duration is unsigned, so a window
                running past midnight needs no special handling here. */}
            {editing === 'duration' && (
              <DurationPicker
                durationMin={shown.durationMin}
                floorMin={minDurationMin}
                bounds={bounds}
                hoursSuffix={t('duration.hoursSuffix')}
                minutesSuffix={t('duration.minutesSuffix')}
                onChange={(durationMin) => edit({ durationMin })}
              />
            )}

            {/* No visible readout of the times: the card behind this sheet
                already states them. The live region isn't a preview — it's the
                only way a screen-reader user hears that moving a wheel changed
                the derived end time. */}
            <div aria-live="polite" className="sr-only">
              {draftSummary.startTime} {t('time.toSeparator')} {draftSummary.endTime}
            </div>

            {conflicts && (
              <p
                role="alert"
                className="text-sm"
                style={{ color: 'var(--ion-color-danger)' }}
              >
                {t('handoff.overlapWarning', {
                  other: t(`handoff.${kind === 'pickup' ? 'dropoff' : 'pickup'}`),
                })}
              </p>
            )}
          </div>
        </IonContent>
        {/* Done lives in a pinned footer rather than at the end of the scroll.
            At large accessibility text sizes the control is taller than the
            sheet, so in-flow it started below the fold and only became
            reachable after a scroll the chef had no reason to expect. */}
        <IonFooter>
          <div className="px-4 pb-6 pt-2">
            <IonButton expand="block" disabled={conflicts} onClick={confirm}>
              {t('handoff.done')}
            </IonButton>
          </div>
        </IonFooter>
      </IonModal>
    </>
  );
}
